import React, { useMemo, useEffect, useState } from "react";
import PropTypes from "prop-types";
import styles from "./TextSlider.module.scss";
import Card from "../Card/Card";

const TextSlider = ({ slides, itemsPerSlide = 3, cardProps = {} }) => {
  // Fensterbreite tracken, um zwischen mobilem und Desktop-Layout zu unterscheiden
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Bei einer Breite unter 768px wird das mobile Layout verwendet
  const isMobile = windowWidth < 768;

  // Für Desktop: Erstelle einen Carousel-Slider mit gruppierten Slides
  const randomId = useMemo(
    () => `carousel-${Math.random().toString(36).substring(2, 11)}`,
    []
  );

  // Gruppiere die Slides für den Carousel-Slider (Desktop)
  const slideGroups = useMemo(() => {
    const groups = [];
    for (let i = 0; i < slides.length; i += itemsPerSlide) {
      groups.push(slides.slice(i, i + itemsPerSlide));
    }
    return groups;
  }, [slides, itemsPerSlide]);

  if (isMobile) {
    // Mobile-Layout: Horizontal scrollbarer Container
    return (
      <div className={styles.TextSliderMobile}>
        <div className="d-flex overflow-auto flex-nowrap px-3 py-3">
          {slides.map((slide, index) => (
            <div
              key={slide.id || index}
              className="flex-shrink-1 me-3"
              style={{ minWidth: "80%" }} // Passt die Breite der Karten an – 80% ist ein Beispielwert
            >
              <Card
                {...cardProps}
                title={slide.title}
                lead={slide.short_desc}
                desc={slide.desc}
                count_up={slide.count_up}
                image={slide.image}
                image_alt={slide.image_alt}
                cta_text={slide.cta_text}
                cta_link={slide.cta_link}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Desktop-Layout: Carousel-Slider
  return (
    <div className={`${styles.TextSlider} position-relative`}>
      <button
        className="carousel-control-prev"
        type="button"
        data-bs-target={`#${randomId}`}
        data-bs-slide="prev"
      >
        <span className="carousel-control-prev-icon" aria-hidden="true"></span>
      </button>

      <div id={randomId} className="carousel slide" data-bs-ride="carousel">
        <div className="carousel-inner px-5 py-3">
          {slideGroups.map((group, groupIndex) => (
            <div
              key={groupIndex}
              className={`carousel-item ${groupIndex === 0 ? "active" : ""}`}
            >
              <div className="row g-4">
                {group.map((slide, index) => (
                  <div
                    key={slide.id || index}
                    className={`col-md-${12 / itemsPerSlide}`}
                  >
                    <Card
                      {...cardProps}
                      title={slide.title}
                      lead={slide.short_desc}
                      desc={slide.desc}
                      count_up={slide.count_up}
                      image={slide.image}
                      image_alt={slide.image_alt}
                      cta_text={slide.cta_text}
                      cta_link={slide.cta_link}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="carousel-indicators mt-3">
          {slideGroups.map((_, index) => (
            <button
              key={index}
              type="button"
              data-bs-target={`#${randomId}`}
              data-bs-slide-to={index}
              className={index === 0 ? "active" : ""}
              aria-label={`Slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      <button
        className="carousel-control-next"
        type="button"
        data-bs-target={`#${randomId}`}
        data-bs-slide="next"
      >
        <span className="carousel-control-next-icon" aria-hidden="true"></span>
      </button>
    </div>
  );
};

TextSlider.propTypes = {
  slides: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string.isRequired,
      desc: PropTypes.string,
      short_desc: PropTypes.string,
      count_up: PropTypes.number,
      image: PropTypes.string,
      image_alt: PropTypes.string,
      cta_text: PropTypes.string,
      cta_link: PropTypes.string,
    })
  ).isRequired,
  itemsPerSlide: PropTypes.number,
  cardProps: PropTypes.object,
};

export default TextSlider;
